import { Link } from 'react-router-dom';
import { Heart, Mail, Shield, TrendingUp, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function About() {
  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <header className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <h1 className="text-2xl font-light tracking-tight" style={{fontFamily: 'Spectral, serif'}}>Stable Stationary</h1>
          </Link>
          <nav className="flex items-center gap-4">
            <Link to="/pricing"><Button variant="ghost" data-testid="nav-pricing-btn">Pricing</Button></Link>
            <Link to="/signup"><Button className="bg-[#B94A3E] hover:bg-[#9d3e33] text-white" data-testid="nav-signup-btn">Start Free</Button></Link>
          </nav>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="mb-16">
          <p className="label-text mb-4">Our Mission</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-light mb-8" style={{fontFamily: 'Spectral, serif'}}>
            We help people show up for the ones they love.
          </h1>
          <p className="text-xl leading-relaxed text-[#1A1A1A]/85 font-light" style={{fontFamily: 'Spectral, serif'}}>
            An estimated <strong>2.7 million children</strong> in the United States have an incarcerated parent. Millions more adults have partners, siblings, or friends inside. For all of them, mail is a lifeline.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mb-20 items-start">
          <div>
            <h2 className="text-3xl mb-6" style={{fontFamily: 'Spectral, serif'}}>Why we built this</h2>
            <div className="space-y-4 text-[#1A1A1A]/85 leading-relaxed">
              <p>
                The people on the outside want to write. They love the person inside. But life gets in the way — work, kids, exhaustion, not knowing what to say.
              </p>
              <p>
                Weeks pass. Then months. The person inside starts to feel forgotten. The relationship strains under silence.
              </p>
              <p>
                Stable Stationary removes the friction. AI helps you draft letters that sound like <em>you</em>. Memory-style photos give your loved one something to look at. Automated mailing means you never have to find an envelope, stamp, or trip to the post office.
              </p>
              <p className="text-[#B94A3E] font-medium">
                We're not writing letters for you. We're helping you keep the promise you already want to keep.
              </p>
            </div>
          </div>

          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <h3 className="text-xl mb-6" style={{fontFamily: 'Spectral, serif'}}>What research shows</h3>
            <div className="space-y-6">
              <div className="border-l-2 border-[#B94A3E] pl-4">
                <p className="text-3xl font-light text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}}>13x</p>
                <p className="text-sm text-[#737373]">Higher rate of maintaining family ties for incarcerated people who receive regular mail</p>
              </div>
              <div className="border-l-2 border-[#B94A3E] pl-4">
                <p className="text-3xl font-light text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}}>39%</p>
                <p className="text-sm text-[#737373]">Lower recidivism rate for those with consistent family contact during incarceration</p>
              </div>
              <div className="border-l-2 border-[#B94A3E] pl-4">
                <p className="text-3xl font-light text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}}>2.7M</p>
                <p className="text-sm text-[#737373]">Children in the US with a parent currently incarcerated</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-20">
          <h2 className="text-3xl mb-8" style={{fontFamily: 'Spectral, serif'}}>What we believe</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-[#E5E0D8] rounded-md p-6">
              <Heart className="w-8 h-8 text-[#B94A3E] mb-4" strokeWidth={1.5} />
              <h3 className="text-lg mb-2" style={{fontFamily: 'Spectral, serif'}}>Connection over judgment</h3>
              <p className="text-sm text-[#737373]">What two adults share is their business. We help you show up — the rest is yours.</p>
            </div>
            <div className="bg-white border border-[#E5E0D8] rounded-md p-6">
              <Shield className="w-8 h-8 text-[#B94A3E] mb-4" strokeWidth={1.5} />
              <h3 className="text-lg mb-2" style={{fontFamily: 'Spectral, serif'}}>Transparency over pretense</h3>
              <p className="text-sm text-[#737373]">Every AI-generated photo is clearly labeled. Recipients always know what's real.</p>
            </div>
            <div className="bg-white border border-[#E5E0D8] rounded-md p-6">
              <Mail className="w-8 h-8 text-[#B94A3E] mb-4" strokeWidth={1.5} />
              <h3 className="text-lg mb-2" style={{fontFamily: 'Spectral, serif'}}>Consistency over perfection</h3>
              <p className="text-sm text-[#737373]">A short letter every week beats a long letter every year. Showing up is the whole thing.</p>
            </div>
          </div>
        </div>

        <div className="bg-[#F5EBE0] border border-[#B94A3E]/20 rounded-md p-10 text-center">
          <h2 className="text-3xl mb-4" style={{fontFamily: 'Spectral, serif'}}>Start free today.</h2>
          <p className="text-[#737373] mb-6 max-w-xl mx-auto">Seven days. Two letters. Four memory photos. No card required. See what showing up feels like.</p>
          <Link to="/signup">
            <Button className="bg-[#B94A3E] hover:bg-[#9d3e33] text-white px-8 py-6 text-lg" data-testid="about-cta-btn">
              Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E5E0D8] bg-white py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-[#737373]">© 2026 Stable Stationary. All rights reserved.</p>
          <p className="text-xs text-[#737373] mt-2">
            <Link to="/compliance" className="underline">Trust & Community Standards</Link> · <Link to="/pricing" className="underline">Pricing</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
